import { useEffect, useState } from "react";
import { honorDisplayBanners, resolveCreativeUrl } from "../../util/adDisplayRules";
import { previewExplain } from "../../util/adsCopy";
import {
    AD_DISCLOSURE,
    APP_SCREENS,
    draftSlotForPlacement,
    ellipsisAdTitle,
    homeCarouselAds,
    placementForScreen,
    screenCopy,
    screenForPlacement,
} from "../../util/adsPhonePreview";

const bannerIdOf = (banner) => banner.id || banner.bannerId || banner.imageUrl;

const AdCard = ({ banner }) => (
    <figure className="ads-iphone-ad-card">
        <img
            className="ads-iphone-ad"
            src={resolveCreativeUrl(banner.imageUrl)}
            alt={banner.title || "Ad"}
        />
        <figcaption>
            <span className="ads-iphone-ad-title">{ellipsisAdTitle(banner.title)}</span>
            <span className="ads-iphone-ad-mark">{AD_DISCLOSURE}</span>
        </figcaption>
    </figure>
);

const SponsorTile = ({ banner }) => (
    <article className="ads-iphone-tile" data-testid="ads-sponsor-tile">
        <img
            src={resolveCreativeUrl(banner.imageUrl)}
            alt={banner.title || "Sponsor logo"}
        />
    </article>
);

const EventDetailsFeed = ({ ads, logos }) => (
    <div className="ads-iphone-feed">
        {ads.map((banner) => (
            <AdCard key={bannerIdOf(banner)} banner={banner} />
        ))}
        <div className="ads-iphone-row">Saturday 9:00 AM</div>
        <div className="ads-iphone-row">Court 2 · Open play</div>
        {logos.length > 0 && (
            <div className="ads-iphone-sponsors-block" data-testid="ads-app-sponsor-tiles">
                <h3>SPONSORS</h3>
                <div className="ads-iphone-sponsor-tiles">
                    {logos.map((banner) => (
                        <SponsorTile key={bannerIdOf(banner)} banner={banner} />
                    ))}
                </div>
            </div>
        )}
    </div>
);

const PhoneScreen = ({
    screen,
    clubName,
    ads,
    logos,
}) => {
    const copy = screenCopy(screen);
    const carousel = screen === "home" ? homeCarouselAds(ads, logos) : ads;

    return (
        <div className="ads-iphone-screen" data-screen={screen}>
            <div className="ads-iphone-status">
                <span>9:41</span>
                <span className="ads-iphone-island" aria-hidden="true" />
                <span>5G</span>
            </div>
            <div className="ads-iphone-nav">
                <strong>{clubName || "Your club"}</strong>
            </div>
            <p className="ads-iphone-screen-title">{copy.title}</p>
            {screen === "home" && carousel.length > 0 && (
                <div className="ads-iphone-ads" data-testid="ads-home-carousel">
                    {carousel.map((banner) => (
                        <AdCard key={bannerIdOf(banner)} banner={banner} />
                    ))}
                </div>
            )}
            {(screen === "events" || screen === "schedule") && (
                <EventDetailsFeed ads={ads} logos={logos} />
            )}
        </div>
    );
};

const AdsAppPreview = ({
    clubName,
    isFreePlan = true,
    sponsorships = [],
    loadDisplay,
    initialScreen = "home",
    initialPlacement,
    compact = false,
    draft,
}) => {
    const startScreen = initialPlacement ? screenForPlacement(initialPlacement) : initialScreen;
    const [screen, setScreen] = useState(startScreen);
    const [ads, setAds] = useState([]);
    const [logos, setLogos] = useState([]);

    useEffect(() => {
        setScreen(initialPlacement ? screenForPlacement(initialPlacement) : initialScreen);
    }, [initialScreen, initialPlacement]);

    useEffect(() => {
        if (!draft?.imageUrl) {
            return;
        }
        const item = {
            id: "draft",
            title: draft.title || "Preview",
            imageUrl: draft.imageUrl,
            placement: draft.placement,
        };
        if (draftSlotForPlacement(draft.placement) === "logo") {
            setAds([]);
            setLogos([item]);
        } else {
            setAds([item]);
            setLogos([]);
        }
        setScreen(screenForPlacement(draft.placement));
    }, [draft]);

    useEffect(() => {
        if (draft?.imageUrl || !loadDisplay) {
            return undefined;
        }
        let cancelled = false;
        const placement = placementForScreen(screen);
        Promise.all([
            loadDisplay(placement),
            loadDisplay("SPONSOR_SECTION"),
        ]).then(([primary, strip]) => {
            if (cancelled) {
                return;
            }
            const rules = { sponsorships, isFreePlan };
            setAds(honorDisplayBanners(primary?.Data || [], { placement, ...rules }));
            setLogos(honorDisplayBanners(strip?.Data || [], { placement: "SPONSOR_SECTION", ...rules }));
        }).catch(() => {
            if (!cancelled) {
                setAds([]);
                setLogos([]);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [draft, loadDisplay, screen, sponsorships, isFreePlan]);

    const copy = screenCopy(screen);
    const explain = previewExplain({ screen, ads, logos, isFreePlan });

    return (
        <div className={`ads-app-preview${compact ? " ads-app-preview--compact" : ""}`} data-testid={compact ? "ads-app-preview-compact" : "ads-preview"}>
            {!compact && (
                <div className="ads-app-preview-copy">
                    <p className="ads-slot-kicker">SportMingle app</p>
                    <h2>How members will see ads</h2>
                    <p className="ads-card-lead">
                        Live banners from the Banners tab — not the companies on Sponsors.
                        Flip Home, Events, and Event Details on the phone. This page does not count as a view.
                    </p>
                    <section className="ads-preview-explain" data-testid="ads-preview-explain">
                        <div>
                            <h3>What we are showing</h3>
                            <p>{explain.what}</p>
                        </div>
                        <div>
                            <h3>Why</h3>
                            <p>{explain.why}</p>
                        </div>
                    </section>
                    <p className="ads-hint">{copy.label} tab. Web Preview does not count as views.</p>
                </div>
            )}
            <div className="ads-iphone" data-testid="ads-app-phone">
                <div className="ads-iphone-frame">
                    <PhoneScreen
                        screen={screen}
                        clubName={clubName}
                        ads={ads}
                        logos={logos}
                    />
                    <div className="ads-iphone-tabbar" role="tablist" aria-label="App screen">
                        {APP_SCREENS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={screen === item.id}
                                className="ads-iphone-tab"
                                disabled={compact}
                                onClick={() => setScreen(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdsAppPreview;
